/* eslint-disable react/prop-types */
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import React from "react";
import { GoogleAuthButton } from "@/components/atoms/Auth/googleAuth";
import { EmailInsteadText } from "./index";
import { Controller, useForm } from "react-hook-form";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import Link from "next/link";
import { useAuth } from "@/hooks/useAuth";
import { Loader2 } from "lucide-react";

const TRIAL_PERIOD_DAYS = 15;

export default function SignupForm({ className, setCurrentTab }) {
  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm();
  const { register: registerApi } = useAuth();

  const onSubmit = async (formData) => {
    const data = {
      email: formData?.email,
      password: formData?.password,
      name: formData?.name,
      password_confirmation: formData?.confirm_password,
      remember: formData?.remember,
    };

    try {
      await registerApi(data);

     
    } catch (err) {
      console.log(err, "error");
    }
  };

  return (
    <>
      <div className="flex flex-col items-center gap-1 text-center mb-8">
        <h1 className="text-[3rem] font-bold capitalize text-foreground">
          Start your {TRIAL_PERIOD_DAYS}-day free trial today
        </h1>
      </div>
      <div className="mb-10">
        <GoogleAuthButton isRegister={true} />
      </div>
      <div className="mb-10">
        <EmailInsteadText />
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className={cn("flex flex-col gap-6", className)}
      >
        <FieldGroup>
          <Field>
            <FieldLabel
              className={"text-[1.4rem] text-card-foreground"}
              htmlFor="name"
            >
              Full Name:{" "}
            </FieldLabel>
            <Input
              id="name"
              type="text"
              placeholder=""
              className="p-4 md:text-[1.6rem] h-16 rounded-xl text-card-foreground"
              name="name"
              {...register("name", {
                required: "Full name is required",
                pattern: {
                  value: /^[A-Za-z ]+$/,
                  message: "Name should contain only letters and spaces",
                },
              })}
              required
            />
            {errors.name && (
              <FieldError className=" text-[1.2rem] mt-1 dark:text-red-500">
                {errors.name.message}
              </FieldError>
            )}
          </Field>

          <Field>
            <FieldLabel
              className={"text-[1.4rem] text-card-foreground"}
              htmlFor="email"
            >
              Email
            </FieldLabel>
            <Input
              id="email"
              type="email"
              placeholder="m@example.com"
              className="p-4 md:text-[1.6rem] h-16 rounded-xl text-card-foreground"
              {...register("email", {
                required: "Email is required",
              })}
              required
            />
            {errors.email && (
              <FieldError className=" text-[1.2rem] mt-1 dark:text-red-500">
                {errors.email.message}
              </FieldError>
            )}
          </Field>
          <Field>
            <FieldLabel
              className={"text-[1.4rem] text-card-foreground"}
              htmlFor="password"
            >
              Password
            </FieldLabel>
            <Input
              id="password"
              type="password"
              className="p-4 md:text-[1.6rem] h-16 rounded-xl text-card-foreground"
              {...register("password", {
                required: "Password is required",
                minLength: {
                  value: 8,
                  message: "Password must be at least 8 characters",
                },
              })}
              required
            />
            {errors.password && (
              <FieldError className=" text-[1.2rem] mt-1 dark:text-red-500">
                {errors.password.message}
              </FieldError>
            )}
          </Field>
          <Field>
            <FieldLabel
              className={"text-[1.4rem] text-card-foreground"}
              htmlFor="confirm_password"
            >
              Confirm Password
            </FieldLabel>
            <Input
              id="confirm_password"
              type="password"
              className="p-4 md:text-[1.6rem] h-16 rounded-xl text-card-foreground"
              {...register("confirm_password", {
                required: "Confirm Password is required",
                validate: (value) =>
                  value === watch("password") || "Passwords do not match",
              })}
              required
            />
            {errors.confirm_password && (
              <FieldError className=" text-[1.2rem] mt-1 dark:text-red-500">
                {errors.confirm_password.message}
              </FieldError>
            )}
          </Field>
          <Controller
            name="acceptTC"
            control={control}
            defaultValue={false}
            rules={{
              required: "Please accept Terms and Policies",
            }}
            render={({ field }) => (
              <div>
                <div className="flex items-center gap-3">
                  <Checkbox
                    className={"scale-150"}
                    id="terms"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                  <Label
                    className={"text-[1.4rem] text-card-foreground"}
                    htmlFor="terms"
                  >
                    Please accept &nbsp;
                    <Link
                      target="_blank"
                      className="font-bold hover:underline"
                      // href="https://www.easygtm.com/policies/terms-and-conditions"
                      href="https://www.maaran.ai/terms-and-conditions"
                    >
                      Terms
                    </Link>{" "}
                    and{" "}
                    <Link
                      // href="https://www.easygtm.com/policies/privacy-policy"
                      href="https://www.maaran.ai/privacy-policy"
                      target="_blank"
                      className="font-bold hover:underline"
                    >
                      Policies
                    </Link>{" "}
                    *
                  </Label>
                </div>
                {errors.acceptTC && (
                  <FieldError className=" text-[1.2rem] mt-1 dark:text-red-500">
                    {errors.acceptTC.message}
                  </FieldError>
                )}
              </div>
            )}
          />

          <Controller
            name="remember"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <div className="flex items-center gap-3">
                <Checkbox
                  id="remember"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  className="scale-150"
                />
                <Label
                  htmlFor="remember"
                  className="text-[1.4rem] text-card-foreground"
                >
                  Remember Me
                </Label>
              </div>
            )}
          />

          <Field>
            <Button
              className="px-8 py-7 text-[1.4rem] rounded-xl uppercase flex items-center gap-4 justify-center"
              type="submit"
              disabled={isSubmitting}
            >
              Start my free trail
              {isSubmitting && (
                <Loader2
                  className="spinner-mini"
                  style={{
                    width: "2rem",
                    height: "2rem",
                  }}
                />
              )}
            </Button>
          </Field>
          <Field>
            <FieldDescription className="text-center text-[1.4rem]">
              Already have an account ?
              <Button
                type="button"
                onClick={() => setCurrentTab("login")}
                variant="secondary"
                className="ml-2  text-[1.4rem] hover:underline underline-offset-8 "
              >
                Login
              </Button>
            </FieldDescription>
          </Field>
        </FieldGroup>
      </form>
    </>
  );
}
